import { Button, HStack, Popover, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdDelete } from "react-icons/md";
import { getThemeColor } from "theme/colors";

interface DeleteConfirmPopoverProps {
  /** Fires after the user confirms the deletion. */
  onConfirm: () => void;
  /** Disables the buttons and shows a spinner while the request runs. */
  loading: boolean;
  /** Bold heading inside the popover, e.g. "Delete this tool?". */
  title: string;
  /** Warning body — should make clear the action is irreversible. */
  message: string;
  /** Label on the outline-red trigger button, e.g. "Delete Tool". */
  triggerLabel: string;
  /** Label on the destructive confirm button. Defaults to triggerLabel. */
  confirmLabel?: string;
  /** Stretch the trigger button to fill its container (for button columns). */
  fullWidth?: boolean;
  /** Disable opening the popover (e.g. while another row action is busy). */
  disabled?: boolean;
}

/**
 * Delete trigger button with a confirmation popover that warns the action is
 * irreversible before calling `onConfirm`. Positioning, portal rendering (never
 * clipped by an ancestor's `overflow: hidden`), auto-flip and close-on-outside
 * are all handled by the shared `Popover` (Chakra/floating-ui). Opens above the
 * trigger by default.
 */
const DeleteConfirmPopover = ({
  onConfirm,
  loading,
  title,
  message,
  triggerLabel,
  confirmLabel,
  fullWidth,
  disabled,
}: DeleteConfirmPopoverProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="top-end"
      width="300px"
      trigger={
        <Button
          intent="danger"
          variant="outline"
          size="sm"
          w={fullWidth ? "full" : undefined}
          loading={loading}
          disabled={disabled}
        >
          <MdDelete />
          {triggerLabel}
        </Button>
      }
    >
      <VStack align="stretch" gap={3}>
        <Text fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {message}
        </Text>
        <HStack justify="flex-end" gap={2}>
          <Button intent="secondary" size="sm" onClick={() => setOpen(false)}>
            {t("app.common.cancel")}
          </Button>
          <Button
            intent="danger"
            size="sm"
            loading={loading}
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            {confirmLabel ?? triggerLabel}
          </Button>
        </HStack>
      </VStack>
    </Popover>
  );
};

export default DeleteConfirmPopover;
